import { marked } from 'marked';

/**
 * Unwraps content from paragraph tags inside a cell,
 * preserving the inner HTML content.
 */
function unwrapParagraphs(cell: Element): void {
  const paragraphs = cell.querySelectorAll('p');
  if (paragraphs.length === 0) {
    return;
  }

  // If there's only one paragraph, replace cell content with paragraph's innerHTML
  if (paragraphs.length === 1) {
    cell.innerHTML = paragraphs[0].innerHTML;
    return;
  }

  // Multiple paragraphs: join with line breaks
  const contents: string[] = [];
  for (const p of paragraphs) {
    contents.push(p.innerHTML);
  }
  cell.innerHTML = contents.join('<br>');
}

/**
 * Transforms TipTap HTML tables to Google Docs compatible format.
 * - Adds border and border-collapse styles to tables
 * - Adds padding to all cells
 * - Adds background color to header cells
 * - Removes colgroup, tbody, thead wrappers
 * - Unwraps content from paragraph tags in cells
 * - Cleans up unnecessary attributes
 */
function transformTablesForGoogleDocs(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const tables = doc.querySelectorAll('table');

  for (const table of tables) {
    // Set table styles for Google Docs compatibility
    table.setAttribute('border', '1');
    table.setAttribute('style', 'border-collapse:collapse;width:100%');

    // Remove colgroup elements
    for (const colgroup of table.querySelectorAll('colgroup')) {
      colgroup.remove();
    }

    // Get all rows from tbody/thead and move them directly under table
    const rows = table.querySelectorAll('tr');
    const tbody = table.querySelector('tbody');
    const thead = table.querySelector('thead');

    // Move rows directly under table
    for (const row of rows) {
      table.appendChild(row);
    }

    // Remove tbody and thead wrappers
    tbody?.remove();
    thead?.remove();

    // Style header cells
    for (const th of table.querySelectorAll('th')) {
      th.setAttribute('style', 'padding:6px;background:#f5f5f5');
      th.removeAttribute('class');
      if (th.getAttribute('colspan') === '1') {
        th.removeAttribute('colspan');
      }
      if (th.getAttribute('rowspan') === '1') {
        th.removeAttribute('rowspan');
      }
      unwrapParagraphs(th);
    }

    // Style data cells
    for (const td of table.querySelectorAll('td')) {
      td.setAttribute('style', 'padding:6px');
      td.removeAttribute('class');
      if (td.getAttribute('colspan') === '1') {
        td.removeAttribute('colspan');
      }
      if (td.getAttribute('rowspan') === '1') {
        td.removeAttribute('rowspan');
      }
      unwrapParagraphs(td);
    }
  }

  return doc.body.innerHTML;
}

/**
 * Copies plain text to the clipboard.
 * Uses a 3-tier fallback strategy to maximize browser compatibility:
 * 1. ClipboardItem with Blob (Safari-compatible)
 * 2. Standard writeText API
 * 3. Legacy execCommand
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (navigator == null) {
    return false;
  }

  // 1. Try Safari-compatible async approach with ClipboardItem
  if (navigator.clipboard?.write != null && typeof ClipboardItem !== 'undefined') {
    try {
      const textBlob = new Blob([text], { type: 'text/plain' });
      const clipboardItem = new ClipboardItem({
        'text/plain': textBlob,
      });

      await navigator.clipboard.write([clipboardItem]);
      return true;
    } catch (_err) {
      // Continue to next method
    }
  } else {
    // Continue to next method
  }

  // 2. Try standard writeText API
  if (navigator.clipboard?.writeText != null) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (_err) {
      // Continue to fallback
    }
  }

  // 3. Fallback to document.execCommand (works in older browsers and some edge cases)
  let textarea: HTMLTextAreaElement | null = null;
  try {
    textarea = document.createElement('textarea');
    textarea.value = text;

    // Prevent scrolling and make invisible
    textarea.style.position = 'fixed';
    textarea.style.top = '0';
    textarea.style.left = '0';
    textarea.style.opacity = '0';
    textarea.style.pointerEvents = 'none';

    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    const success = document.execCommand('copy');
    return success;
  } catch (_err) {
    return false;
  } finally {
    // Always remove the textarea from DOM to prevent element leak
    if (textarea?.parentNode != null) {
      textarea.parentNode.removeChild(textarea);
    }
  }
}

/**
 * Safari-compatible async clipboard copy.
 * Use this when you need to fetch or process data asynchronously before copying.
 *
 * This works by passing a Promise to ClipboardItem, which Safari accepts
 * because the clipboard API is called synchronously (within user gesture),
 * even though the Promise resolves asynchronously.
 *
 * @example
 * // In a click handler:
 * button.onclick = () => {
 *   copyToClipboardAsync(
 *     fetch('/api/data').then(r => r.text())
 *   );
 * };
 */
export async function copyToClipboardAsync(textPromise: Promise<string>): Promise<boolean> {
  if (navigator?.clipboard?.write == null || typeof ClipboardItem === 'undefined') {
    try {
      const text = await textPromise;
      return copyToClipboard(text);
    } catch (_err) {
      return false;
    }
  }

  try {
    // The key: pass the Promise to ClipboardItem
    // ClipboardItem will handle the async resolution internally
    const clipboardItem = new ClipboardItem({
      'text/plain': textPromise.then(function (text) {
        return new Blob([text], { type: 'text/plain' });
      }),
    });

    await navigator.clipboard.write([clipboardItem]);
    return true;
  } catch (_err) {
    // Fallback: wait for promise and try standard copy
    try {
      const text = await textPromise;
      return copyToClipboard(text);
    } catch (_fallbackErr) {
      return false;
    }
  }
}

/**
 * Copies rich text (HTML) to the clipboard from markdown.
 * Converts markdown to HTML and copies both HTML and plain text versions.
 */
export async function copyToClipboardRichText(text: string): Promise<boolean> {
  if (navigator == null) {
    return false;
  }

  if (typeof ClipboardItem === 'undefined') {
    return copyToClipboard(text);
  }

  try {
    const html = await marked.parse(text);
    const transformedHtml = transformTablesForGoogleDocs(html);

    const blobHtml = new Blob([transformedHtml], { type: 'text/html' });
    const blobText = new Blob([text], { type: 'text/plain' });

    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': blobHtml,
        'text/plain': blobText,
      }),
    ]);

    return true;
  } catch (_err) {
    // Fallback to plain text copy
    return copyToClipboard(text);
  }
}

/**
 * Safari-compatible async rich text clipboard copy.
 * Pass a Promise that resolves to text (markdown), and it will be converted to HTML
 * and copied with proper formatting.
 *
 * This maintains the user gesture context in Safari by calling the clipboard API
 * synchronously, while the Promise resolves asynchronously internally.
 */
export async function copyToClipboardRichTextAsync(textPromise: Promise<string>): Promise<boolean> {
  if (navigator?.clipboard?.write == null || typeof ClipboardItem === 'undefined') {
    try {
      const text = await textPromise;
      return copyToClipboardRichText(text);
    } catch (_err) {
      return false;
    }
  }

  try {
    // Convert the text Promise into HTML and plain text Blobs
    const htmlPromise = textPromise.then(async function (text) {
      const html = await marked.parse(text);
      const transformedHtml = transformTablesForGoogleDocs(html);
      return new Blob([transformedHtml], { type: 'text/html' });
    });

    const textBlobPromise = textPromise.then(function (text) {
      return new Blob([text], { type: 'text/plain' });
    });

    const clipboardItem = new ClipboardItem({
      'text/html': htmlPromise,
      'text/plain': textBlobPromise,
    });

    await navigator.clipboard.write([clipboardItem]);
    return true;
  } catch (_err) {
    // Fallback: wait for promise and try standard copy
    try {
      const text = await textPromise;
      return copyToClipboardRichText(text);
    } catch (fallbackErr) {
      // eslint-disable-next-line no-console
      console.error('[Clipboard] Fallback also failed:', fallbackErr);
      return false;
    }
  }
}
